import _extends from '@babel/runtime/helpers/extends';
import _defineProperty from '@babel/runtime/helpers/defineProperty';
import React, { Component, createRef } from 'react';
import PT from 'prop-types';
import Fuse from 'fuse.js';
import styled, { css } from 'styled-components';
import { SearchInput, useIsMobile, isMobileStyleMixin } from '@amzn/storm-ui';
import { n as noop } from '../LocalModules/storm-ui-utils/dist/esm/noop-ea7cc233-62dc80e0.js';

const WithHideLabel = ({
  hideSearchInputLabel,
  mobileFullScreen,
  children
}) => {
  const isMobile = useIsMobile();
  // packages/storm-ui/src/Dropdown/DropdownIsMobileContext.tsx
  const dropdownIsMobile = mobileFullScreen && isMobile || false;
  return /*#__PURE__*/React.createElement(React.Fragment, null, children({
    hideLabel: typeof hideSearchInputLabel === 'boolean' ? hideSearchInputLabel : !dropdownIsMobile,
    dropdownIsMobile
  }));
};
WithHideLabel.defaultProps = {
  hideSearchInputLabel: undefined,
  mobileFullScreen: undefined
};
const FilterWrapper = /*#__PURE__*/styled.div.withConfig({
  componentId: "sc-storm-ui-30037988__sc-128u5fh-0"
})(["", ""], ({
  noPadding
}) => !noPadding && css(["padding:", ";", ""], ({
  theme
}) => theme.spacing.mini, isMobileStyleMixin(css(["padding:", ";"], ({
  theme
}) => theme.mobile.spacing.mini))));
const withFilter = WrapperComponent => {
  var _class;
  return _class = class WithFilterComponent extends Component {
    constructor(props) {
      super(props);
      _defineProperty(this, "filterRef", /*#__PURE__*/createRef());
      _defineProperty(this, "handleOpen", () => {
        const {
          onFilterClear,
          onOpen,
          persistFilterState
        } = this.props;
        if (!persistFilterState) {
          this.setState({
            filterValue: ''
          });
          if (onFilterClear) {
            onFilterClear('');
          }
        }
        if (onOpen) {
          onOpen();
        }
        window.requestAnimationFrame(() => {
          var _this$filterRef;
          (_this$filterRef = this.filterRef) === null || _this$filterRef === void 0 || (_this$filterRef = _this$filterRef.current) === null || _this$filterRef === void 0 || _this$filterRef.focus();
        });
      });
      _defineProperty(this, "handleFilterChange", event => {
        const {
          items = [],
          options,
          onFilterChange
        } = this.props;
        const filterValue = event.target.value;
        const mergedOptions = {
          ...WithFilterComponent.defaultProps.options,
          ...options
        };
        const fuse = new Fuse(items, mergedOptions);
        const results = filterValue ? fuse.search(filterValue) : items;
        this.setState({
          filterValue
        });
        if (onFilterChange) {
          onFilterChange(results, filterValue, event);
        }
      });
      _defineProperty(this, "handleFilterClear", () => {
        const {
          onFilterClear
        } = this.props;
        this.setState({
          filterValue: ''
        });
        if (onFilterClear) {
          onFilterClear('');
        }
        if (this.filterRef) {
          var _this$filterRef2;
          (_this$filterRef2 = this.filterRef) === null || _this$filterRef2 === void 0 || (_this$filterRef2 = _this$filterRef2.current) === null || _this$filterRef2 === void 0 || _this$filterRef2.focus();
        }
      });
      _defineProperty(this, "handleFilterKeyDown", event => {
        const {
          key
        } = event;
        if (key === ' ' || key === 'Enter') {
          event.stopPropagation();
        }
      });
      _defineProperty(this, "renderFilter", () => {
        const {
          searchInputId,
          clearButtonLabel,
          hideSearchInputLabel,
          placeholder,
          preRender,
          searchInputLabel,
          mobileFullScreen,
          taktId,
          taktValue
        } = this.props;
        return /*#__PURE__*/React.createElement(WithHideLabel, {
          hideSearchInputLabel: hideSearchInputLabel,
          mobileFullScreen: mobileFullScreen
        }, ({
          hideLabel,
          dropdownIsMobile
        }) => /*#__PURE__*/React.createElement(React.Fragment, null, preRender && preRender(), /*#__PURE__*/React.createElement(FilterWrapper, {
          noPadding: dropdownIsMobile
        }, /*#__PURE__*/React.createElement(SearchInput, {
          id: searchInputId,
          placeholder: placeholder,
          fullWidth: true,
          inputRef: this.filterRef,
          value: this.state.filterValue,
          onClear: this.handleFilterClear,
          onChange: this.handleFilterChange,
          clearButtonLabel: clearButtonLabel,
          onKeyDown: this.handleFilterKeyDown,
          hideLabel: hideLabel,
          label: searchInputLabel,
          taktId: taktId,
          taktValue: taktValue
        }))));
      });
      this.state = {
        filterValue: ''
      };
    }
    render() {
      const {
        searchInputId,
        clearButtonLabel,
        onFilterChange,
        onFilterClear,
        items,
        options,
        placeholder,
        hideSearchInputLabel,
        searchInputLabel,
        ...rest
      } = this.props;
      return /*#__PURE__*/React.createElement(WrapperComponent, _extends({}, rest, {
        preRender: this.renderFilter,
        onOpen: this.handleOpen
      }));
    }
  }, _defineProperty(_class, "propTypes", {
    /**
     * Unique id for the search <input> element.
     */
    searchInputId: PT.string.isRequired,
    /**
    * Placeholder text for the search <input> element.
    */
    placeholder: PT.string,
    /**
    * Callback that returns results of fuzzy search on each input state change.
    */
    onFilterChange: PT.func,
    /**
    * Callback that is called when the clear button is clicked on the filter input.
    */
    onFilterClear: PT.func,
    /**
    * Callback that is called when the filter dropdown is opened.
    */
    onOpen: PT.func,
    /**
    * The list of items to be displayed in the dropdown.
    */
    items: PT.arrayOf(PT.object).isRequired,
    /**
    * Use this to provide a screen reader label for the search <input> clear button.
    */
    clearButtonLabel: PT.string,
    /**
    * Options for customizing [Fuse.js](https://fusejs.io/). Sensible defaults are already declared. Define the object properties
    * you want to search with using the `keys` option.
    */
    options: PT.shape({
      shouldSort: PT.bool,
      threshold: PT.number,
      location: PT.number,
      distance: PT.number,
      maxPatternLength: PT.number,
      minMatchCharLength: PT.number,
      keys: PT.arrayOf(PT.string)
    }),
    /**
    * Render prop for rendering components into the DOM before the filter dropdown.
    */
    preRender: PT.func,
    /**
    * Labels are required for accessibility but can be visually hidden using this prop.
    */
    hideSearchInputLabel: PT.bool,
    /**
    * Used to render dropdown in SecondaryView on mobile device.
    */
    mobileFullScreen: PT.bool,
    /**
    * Custom label for the search <input> element.
    */
    searchInputLabel: PT.string,
    /**
    * Used to render dropdown button with no background.
    */
    transparentButton: PT.bool,
    /**
     * By default, the text input will clear and onFilterClear will be called when opening the dropdown.
     * Enabling this prop will circumvent this behavior.
     */
    persistFilterState: PT.bool
  }), _defineProperty(_class, "defaultProps", {
    placeholder: '',
    onFilterChange: noop,
    onFilterClear: noop,
    onOpen: noop,
    preRender: noop,
    clearButtonLabel: 'Clear search terms',
    options: {
      shouldSort: true,
      threshold: 0.6,
      location: 0,
      distance: 100,
      maxPatternLength: 32,
      minMatchCharLength: 1,
      keys: ['label', 'value']
    },
    hideSearchInputLabel: undefined,
    mobileFullScreen: undefined,
    searchInputLabel: undefined,
    transparentButton: false,
    persistFilterState: false
  }), _class;
};
var WithFilterComponent = withFilter;

export { WithFilterComponent as W };
//# sourceMappingURL=withFilter-883f0753.js.map
